import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { Tender, Tendering, TenderingIndexing, TenderIndexing } from "../../../models/tendering";
import { Button, Card, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Title3, Subtitle2, Dialog, DialogSurface, DialogTitle, DialogActions, CardPreview, MessageBar, MessageBarBody, Spinner } from "@fluentui/react-components";
import { updateTenderIndexing } from "../../../services/tenderingService";
import { useAppContext } from "../../../AppContext";
import { getTodayKey, toDateKey } from "../../../utils/tendering-utils";

interface TenderContractProps {
    tenderDocument: Tender;
    tenderingDocument: Tendering;
    updateTender?: Dispatch<SetStateAction<Tender | undefined>>;
}

const TenderContract: FC<TenderContractProps> = (props: TenderContractProps) => {
    const { user } = useAppContext();
    const [openIndexing, setOpenIndexing] = useState<TenderingIndexing | null>(null);
    const [requested, setRequested] = useState<boolean>(false);
    const [mergedData, setMergedData] = useState<any[]>([]);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [isRequesting, setIsRequesting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState<string | null>(null);

    useEffect(() => {
        if (props.tenderingDocument.indexing) {
            setOpenIndexingState(props.tenderDocument.indexing);
            mergeIndexingData(props.tenderDocument.indexing);
        }
    }, [props.tenderingDocument, props.tenderDocument]);

    // The open indexing window closes at the end (23:59:59) of its endDate.
    useEffect(() => {
        if (!openIndexing?.endDate) {
            setTimeLeft(null);
            return;
        }

        const deadline = new Date(`${toDateKey(openIndexing.endDate)}T23:59:59.999`);

        const tick = () => {
            const diffMs = deadline.getTime() - Date.now();
            if (diffMs <= 0) {
                setTimeLeft("00:00:00");
                return;
            }

            const totalSeconds = Math.floor(diffMs / 1000);
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const pad = (n: number) => n.toString().padStart(2, '0');

            setTimeLeft(days > 0
                ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
                : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
        };

        tick();
        const intervalID = setInterval(tick, 1000);
        return () => clearInterval(intervalID);
    }, [openIndexing]);

    const setOpenIndexingState = (tenderIndexing?: TenderIndexing[]): void => {
        if (!props.tenderingDocument.indexing) return;
        const today = getTodayKey();

        props.tenderingDocument.indexing.forEach(indexing => {
            if (indexing.startDate && indexing.endDate &&
                today >= toDateKey(indexing.startDate) && today <= toDateKey(indexing.endDate)) {
                setOpenIndexing(indexing);
                setRequested(!!tenderIndexing?.some(t => Number(t.indexingID) === indexing.indexingID));
            }
        });
    };

    const mergeIndexingData = (tenderIndexing?: TenderIndexing[]): void => {
        const merged: any[] = [];
        const today = getTodayKey();
        
        if (!props.tenderingDocument.indexing) return;

        for (const indexing of props.tenderingDocument.indexing) {
            if (indexing.startDate && today >= toDateKey(indexing.startDate)) {
                const request = tenderIndexing?.find(i => Number(i.indexingID) === indexing.indexingID);
                merged.push({ ...indexing, ...request });
            }
        }
        
        setMergedData(merged);
    };

    const generateUUID = (): string => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    const requestIndexing = async (): Promise<void> => {
        if (!openIndexing || !user || requested || isRequesting) return;

        const indexing: TenderIndexing = {
            ...openIndexing,
            requestDate: new Date(),
            user: user.user.email,
            tenderName: props.tenderDocument.tenderName,
            tenderID: props.tenderDocument.tenderID,
            receipt: generateUUID()
        };

        const updatedIndexing = [indexing, ...(props.tenderDocument.indexing || [])];
        const updatedTender: Tender = { ...props.tenderDocument, indexing: updatedIndexing };

        setIsRequesting(true);
        setErrorMessage(null);
        try {
            await updateTenderIndexing(updatedTender);
            mergeIndexingData(updatedIndexing);
            setRequested(true);
            props.updateTender?.(updatedTender);
            setShowConfirmation(true);
        } catch (error) {
            console.error("Error requesting indexing:", error);
            setErrorMessage("Det gick inte att begära indexering. Försök igen.");
        } finally {
            setIsRequesting(false);
        }
    };

    return (
        <>
            <div className="flex flex-col gap-4">
                <Card>
                    <CardPreview>
                      <Title3>
                          {openIndexing ? openIndexing.indexingName : 'Ingen öppen indexering'}
                      </Title3>
                    </CardPreview>
                    
                    <div className="p-4">
                        {openIndexing ? (
                            <div className="flex flex-col gap-4">
                                <div 
                                    dangerouslySetInnerHTML={{ 
                                        __html: openIndexing.indexingDescription || ''
                                    }} 
                                />
                                
                                <div className="flex flex-col gap-2">
                                    {errorMessage && (
                                        <MessageBar intent="error">
                                            <MessageBarBody>{errorMessage}</MessageBarBody>
                                        </MessageBar>
                                    )}
                                    {!requested ? (
                                        <>
                                            <Button 
                                                appearance="primary" 
                                                onClick={requestIndexing}
                                                className="w-fit"
                                                disabled={isRequesting}
                                                icon={isRequesting ? <Spinner size="tiny" /> : undefined}
                                            >
                                                BEGÄR INDEXERING
                                            </Button>
                                            {timeLeft && (
                                                <Text size={200} italic>
                                                    Tid kvar att begära indexering: {timeLeft}
                                                </Text>
                                            )}
                                        </>
                                    ) : (
                                        <Title3>
                                            <Text italic>Indexering begärd</Text>
                                        </Title3>
                                    )}
                                </div>

                                {openIndexing.prices && openIndexing.prices.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        <Subtitle2>Priser som indexeras:</Subtitle2>
                                        {openIndexing.prices.map((price, index) => (
                                            <div key={index} className="border-l-4 border-blue-500 pl-4">
                                                <Text weight="semibold">{price.heading}</Text>
                                                <Text>{price.text}</Text>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Text>Ingen öppen indexering...</Text>
                        )}
                    </div>
                </Card>

                <Card>
                    <CardPreview>
                        <Title3>Indexeringar</Title3>
                    </CardPreview>
                    
                    <div className="p-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        <Subtitle2>Indexering</Subtitle2>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Subtitle2>Datum</Subtitle2>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Subtitle2>Användare</Subtitle2>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Subtitle2>Kvitto</Subtitle2>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {mergedData.map((element, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Text>{element.indexingName}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text>
                                                {element.requestDate 
                                                    ? new Date(element.requestDate).toLocaleDateString('sv-SE')
                                                    : '-'
                                                }
                                            </Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text className="break-all">{element.user || 'Ej Begärd'}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text>{element.receipt || '-'}</Text>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            </div>

            {/* Confirmation Dialog */}
            <Dialog 
                open={showConfirmation} 
                onOpenChange={(_, data) => !data.open && setShowConfirmation(false)}
            >
                <DialogSurface>
                    <DialogTitle>Indexering begärd</DialogTitle>
                    <DialogActions>
                        <Button 
                            appearance="primary" 
                            onClick={() => setShowConfirmation(false)}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default TenderContract;